import { useCallback, useEffect, useRef } from 'react';

import type { JSONContent, Editor as TiptapEditor } from '@tiptap/core';

import { shouldShowEmptyState } from '../../utils/editorContent';

type UseServerContentSyncOptions = {
  editor: TiptapEditor | null;
  contentVersion: string | undefined;
  initialContent: JSONContent | undefined;
  isRealtimeEditing: boolean;
  markContentAsPersisted: (content: JSONContent) => void;
  onUpdate?: (editor: TiptapEditor, hasContent: boolean) => void;
};

export const useServerContentSync = ({
  editor,
  contentVersion,
  initialContent,
  isRealtimeEditing,
  markContentAsPersisted,
  onUpdate,
}: UseServerContentSyncOptions) => {
  const hasUserEditedRef = useRef(false);
  const acceptedVersionRef = useRef<string | undefined>(contentVersion);

  const resetOnCreate = useCallback((version: string | undefined) => {
    hasUserEditedRef.current = false;
    acceptedVersionRef.current = version;
  }, []);

  const markEdited = useCallback(() => {
    hasUserEditedRef.current = true;
  }, []);

  useEffect(() => {
    if (!editor || !initialContent || isRealtimeEditing) return;
    if (contentVersion === acceptedVersionRef.current) return;
    if (hasUserEditedRef.current) return;

    acceptedVersionRef.current = contentVersion;
    editor.commands.setContent(initialContent, { emitUpdate: false });
    markContentAsPersisted(initialContent);

    onUpdate?.(editor, !shouldShowEmptyState(initialContent));
  }, [
    editor,
    contentVersion,
    initialContent,
    isRealtimeEditing,
    markContentAsPersisted,
    onUpdate,
  ]);

  return { resetOnCreate, markEdited };
};
